import { useCart } from "../context/CartContext";
import { Phone, Mail, Globe } from "lucide-react";

const FOOTER_CATS = [
  { slug: "guest-amenities", name: "Guest Amenities" },
  { slug: "fragrance-solutions", name: "Fragrance Solutions" },
  { slug: "housekeeping-chemicals", name: "Housekeeping Chemicals" },
  { slug: "housekeeping-tools", name: "Housekeeping Tools" },
  { slug: "housekeeping-machines", name: "Housekeeping Machines" },
];

export const Footer = ({ onSelect = () => {} }) => {
  const { setOpen } = useCart();
  return (
    <footer id="contact" data-testid="site-footer" className="border-t border-[#221533]/10 bg-white">
      <div className="max-w-[1500px] mx-auto px-5 sm:px-6 lg:px-12 py-16 sm:py-20 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <p className="font-clash text-4xl sm:text-5xl font-semibold text-[#74427F] tracking-tight">
            ZALTA<sup className="text-[#E63946] text-lg">®</sup>
          </p>
          <p className="mt-6 text-[#221533]/55 font-light leading-relaxed max-w-sm">
            Housekeeping Chemicals &amp; Herbal Cosmetics — manufactured in India,
            supplied direct from factory to institutions for over a decade.
          </p>
        </div>
        <div className="lg:col-span-3">
          <p className="font-mono2 text-[10px] tracking-[0.3em] uppercase text-[#221533]/40 mb-6">Catalogue</p>
          <div className="flex flex-col gap-1.5">
            {FOOTER_CATS.map((c) => (
              <button
                key={c.slug}
                data-testid={`footer-link-${c.slug}`}
                onClick={() => onSelect(c.slug)}
                className="group text-left text-sm font-manrope font-medium text-[#221533]/60 px-3 py-2 -mx-3 rounded-md hover:bg-[#8E5A9E] hover:text-white hover:shadow-[0_8px_18px_-6px_rgba(142,90,158,0.5)] transition-[background-color,color,box-shadow] duration-300 flex items-center justify-between"
              >
                {c.name}
                <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">→</span>
              </button>
            ))}
          </div>
        </div>
        <div className="lg:col-span-4">
          <p className="font-mono2 text-[10px] tracking-[0.3em] uppercase text-[#221533]/40 mb-6">Direct Ordering</p>
          <p className="text-sm text-[#221533]/60 font-light leading-relaxed mb-6">
            Founder-led sales — speak directly with our team for bulk pricing,
            standing orders and SDS/MSDS documentation.
          </p>
          <div className="flex items-center gap-4 mb-4">
            <img
              src={`${process.env.REACT_APP_BACKEND_URL}/api/files/zalta/uploads/products/7079acdc-53f8-452f-bb9d-9a8f5e72a551.jpeg`}
              alt="Paramjeet Singh Ahluwalia — Founder, ZALTA"
              data-testid="footer-founder-photo"
              className="w-14 h-14 rounded-full object-cover object-top border-2 border-[#8E5A9E]/40 shrink-0"
            />
            <p className="font-clash text-xl text-[#221533]">Paramjeet Singh Ahluwalia — Founder</p>
          </div>
          <div className="space-y-2.5 mb-6">
            <p className="text-sm text-[#221533]/60 font-light">
              01 Deen Dayal Nagar, Gwalior,<br />Madhya Pradesh — 474003
            </p>
            {[
              { icon: Phone, label: "+91 81038 03294", href: "tel:+918103803294", testid: "footer-phone-link", external: false },
              { icon: Mail, label: "info@zalta.org", href: "mailto:info@zalta.org", testid: "footer-email-link", external: false },
              { icon: Globe, label: "www.zalta.org", href: "https://www.zalta.org", testid: "footer-website-link", external: true },
            ].map((c) => (
              <a
                key={c.label}
                href={c.href}
                data-testid={c.testid}
                {...(c.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                className="group flex items-center gap-3.5 bg-[#FAF8FC] border border-[#221533]/10 hover:border-[#8E5A9E] rounded-xl px-4 py-3 transition-[border-color,box-shadow,transform] duration-300 hover:shadow-[0_10px_25px_-10px_rgba(142,90,158,0.45)] hover:-translate-y-0.5 active:scale-[0.98]"
              >
                <span className="w-9 h-9 shrink-0 rounded-lg bg-[#8E5A9E]/10 text-[#74427F] flex items-center justify-center group-hover:bg-[#8E5A9E] group-hover:text-white transition-colors duration-300">
                  <c.icon size={16} />
                </span>
                <span className="font-manrope font-semibold text-sm text-[#221533]/80 group-hover:text-[#74427F] transition-colors">
                  {c.label}
                </span>
              </a>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <button
              data-testid="footer-quote-btn"
              onClick={() => setOpen(true)}
              className="bg-[#8E5A9E] hover:bg-[#74427F] text-white font-mono2 text-xs tracking-[0.2em] uppercase px-8 py-4 rounded-md transition-colors duration-300 active:scale-95"
            >
              Request Bulk Quote
            </button>
            <a href="/admin" data-testid="footer-admin-link" className="font-mono2 text-[10px] tracking-[0.2em] uppercase text-[#221533]/35 hover:text-[#74427F] transition-colors">
              Admin Login →
            </a>
          </div>
        </div>
      </div>
      <div className="border-t border-[#221533]/10">
        <div className="max-w-[1500px] mx-auto px-5 sm:px-6 lg:px-12 py-6 flex flex-col sm:flex-row justify-between gap-3">
          <span className="font-mono2 text-[10px] tracking-[0.25em] uppercase text-[#221533]/35">
            © {new Date().getFullYear()} ZALTA® — All rights reserved
          </span>
          <span className="font-mono2 text-[10px] tracking-[0.25em] uppercase text-[#221533]/35">
            Pan-India · Factory → ZALTA → Client
          </span>
        </div>
      </div>
    </footer>
  );
};
